import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Table, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { IconExternalLink } from '@tabler/icons-react';

// Component to display all checkpoints where a measure appears
const MeasureCheckpointList = ({ measureId, fallbackUnitId = null, onCheckpointNavigate = null }) => {
  const navigate = useNavigate();
  // Select all checkpoints from the store
  const checkpoints = useSelector((state) => state.checkpoints.checkpoints);
  
  // Find all checkpoints that contain this measure
  const checkpointsWithStatus = useMemo(() => {
    return checkpoints.flatMap((checkpoint) => {
      if (checkpoint.checkpointType !== 'measures') return [];

      let matchedMeasure = null;

      checkpoint.actionAreas.some((actionArea) =>
        actionArea.measures.some((measure) => {
          if (measure.id === measureId) {
            matchedMeasure = measure;
            return true;
          }

          return false;
        })
      );

      if (!matchedMeasure) {
        return [];
      }

      return [{
        id: checkpoint.id,
        title: checkpoint.title,
        dueDate: checkpoint.dueDate,
        status: matchedMeasure.status,
        statusId: matchedMeasure.status_id,
        unitId: matchedMeasure.unit ?? fallbackUnitId,
      }];
    });
  }, [checkpoints, fallbackUnitId, measureId]);
  
  // Function to format date in German format (DD.MM.YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) return '-';
    
    // Format as DD.MM.YYYY
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is zero-based
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  };
  
  // Function to get badge color based on status
  const getStatusBadgeStyle = (status) => {
    if (!status) {
      return {
        backgroundColor: 'var(--status-no-status)',
        color: '#ffffff',
      };
    }
    
    switch (status) {
      case 'gestoppt':
      case 'schwierigkeiten':
        return {
          backgroundColor: 'var(--status-stopped)',
          color: 'var(--brand-black-11131c, #11131C)',
        };
      case 'nicht begonnen':
        return {
          backgroundColor: 'var(--status-not-started)',
          color: '#ffffff',
        };
      case 'in Bearbeitung - verzögert':
      case 'verzögert':
        return {
          backgroundColor: 'var(--status-negative)',
          color: '#ffffff',
        };
      case 'in Bearbeitung - nach Plan':
      case 'planmäßig':
        return {
          backgroundColor: 'var(--status-on-track)',
          color: 'var(--brand-black-11131c, #11131C)',
        };
      case 'abgeschlossen':
        return {
          backgroundColor: 'var(--status-positive)',
          color: '#ffffff',
        };
      default:
        return {
          backgroundColor: 'var(--status-no-status)',
          color: '#ffffff',
        };
    }
  };

  // Navigate to the checkpoints page with the selected checkpoint
  const handleCheckpointClick = useCallback((checkpointId, targetUnitId) => {
    onCheckpointNavigate?.();

    const searchParams = new URLSearchParams();
    const resolvedUnitId = targetUnitId ?? fallbackUnitId;

    if (resolvedUnitId !== null && resolvedUnitId !== undefined && resolvedUnitId !== '') {
      searchParams.set('unitId', String(resolvedUnitId));
    }

    searchParams.set('checkpointId', String(checkpointId));

    navigate({
      pathname: '/checkpoints',
      search: `?${searchParams.toString()}`,
    });
  }, [fallbackUnitId, navigate, onCheckpointNavigate]);
  
  if (checkpointsWithStatus.length === 0) {
    return (
      <div className="text-muted fst-italic">
        Diese Maßnahme ist noch keinem Checkpoint zugeordnet.
      </div>
    );
  }
  
  return (
    <div className="measure-checkpoints-container">
      <Table hover responsive>
        <thead>
          <tr>
            <th style={{ width: '25%' }}>Datum</th>
            <th style={{ width: '50%' }}>Checkpoint</th>
            <th style={{ width: '25%' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {checkpointsWithStatus.map(checkpoint => (
            <tr key={checkpoint.id}>
              <td>{formatDate(checkpoint.dueDate)}</td>
              <td>{checkpoint.title}</td>
              <td>
                <div className="d-flex align-items-center">
                  <Badge 
                    bg=""
                    className="me-2"
                    style={{
                      cursor: 'pointer',
                      ...getStatusBadgeStyle(checkpoint.status),
                    }}
                    onClick={() => handleCheckpointClick(checkpoint.id, checkpoint.unitId)}
                  >
                    {checkpoint.status || 'Kein Status'}
                  </Badge>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-checkpoint-${checkpoint.id}`}>
                        Zum Checkpoint wechseln
                      </Tooltip>
                    }
                  >
                    <div 
                      style={{ cursor: 'pointer' }} 
                      onClick={() => handleCheckpointClick(checkpoint.id, checkpoint.unitId)}
                      className="d-flex align-items-center"
                    >
                      <IconExternalLink size={18} stroke={1.5} />
                    </div>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default MeasureCheckpointList;
